import { hash } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { resourceUndoUpserters, resourceUpserters } from 'app-components/flux/core';
import { coreActions } from 'app-components/flux/core-actions';
import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';
import {
  deleteScheduledTransaction,
  deleteScheduledTransactionFailure,
  deleteScheduledTransactionSuccess,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsActions';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';

import * as actions from './transactionsActions';
import * as transactionsTypes from './transactionsTypes';

const reducer = handleActions<
  QcsSyncResourceStore<transactionsTypes.TransactionProps, transactionsTypes.TransactionStoreProps>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.getTransactions>
  | ReturnType<typeof actions.getTransactionsSuccess>
  | ReturnType<typeof actions.getTransactionsFailure>
  | ReturnType<typeof actions.createTransaction>
  | ReturnType<typeof actions.createTransactionSuccess>
  | ReturnType<typeof actions.updateTransaction>
  | ReturnType<typeof actions.updateTransactionSuccess>
  | ReturnType<typeof actions.deleteTransaction>
  | ReturnType<typeof actions.deleteTransactionSuccess>
  | ReturnType<typeof actions.batchTransactions>
  | ReturnType<typeof actions.batchTransactionsSuccess>
  | ReturnType<typeof actions.applyAccountsChangesClone>
  | ReturnType<typeof actions.performTransactionAction>
  | ReturnType<typeof actions.getTransactionsEarliestDateOnSucess>
  | ReturnType<typeof actions.applyTransactionsChanges>
  | ReturnType<typeof actions.initiateA2ATransferSuccess>
  | ReturnType<typeof actions.initiateA2ATransferFailure>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.mergeDeep({
            payee: hash(resource.payee).toString(),
            memo: hash(resource.memo).toString(),
            ...(resource.check && {
              check: {
                memo: hash(resource.check.memo).toString(),
              },
            }),
            ...(resource.cpData && {
              cpData: {
                payee: hash(resource.cpData.payee).toString(),
                inferredPayee: hash(resource.cpData.inferredPayee).toString(),
                memo: hash(resource.cpData.memo).toString(),
              },
            }),
          }),
        ),
      ),

    [actions.getTransactions.type]: (state) => state.set('isLoading', true),
    [actions.getTransactionsSuccess.type]: (state, action) => {
      const dateOnAfter = action.meta?.config?.axiosConfig?.params?.dateOnAfter;
      const dateOnBefore = action.meta?.config?.axiosConfig?.params?.dateOnBefore;
      const newState = resourceUpserters.upsertResources(
        state,
        'set' in action.payload ? action.payload.set('doMerge', false) : { ...action.payload, doMerge: false },
      );
      let lastSyncDate = newState.lastSyncDate;
      if (dateOnBefore) {
        lastSyncDate = state.lastSyncDate; // do not update if specific chunk loaded
      }
      const resourcesById = newState.resourcesById.filter(
        (transaction) => !dateOnAfter || (transaction.postedOn ?? '') > dateOnAfter,
      );
      return newState.merge({
        resourcesById: resourcesById.size === newState.resourcesById.size ? newState.resourcesById : resourcesById,
        dateOnAfter,
        lastSyncDate,
      });
    },
    [actions.getTransactionsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    [actions.createTransaction.type]: (state, { payload }) => resourceUpserters.upsertResource(state, payload),
    [actions.createTransactionSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteTransaction),

    [actions.updateTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateTransaction),
    [actions.updateTransactionSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResource(state, payload, undefined, undefined, 'default'),

    [actions.deleteTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createTransaction),
    [actions.deleteTransactionSuccess.type]: (state, { payload }) => resourceUpserters.upsertResource(state, payload),

    [actions.batchTransactions.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchTransactions, false),
    [actions.batchTransactionsSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchTransactions, true),

    // accounts cascade deletion
    [actions.applyAccountsChangesClone.type]: (state, { payload, asyncDispatch }) => {
      const deletedAccountIds = payload.filter((account) => account.isDeleted).map((account) => account.id);
      if (deletedAccountIds.length) {
        asyncDispatch(actions.getTransactions(undefined, { context: 'applyAccountsChanges' })); /// TODO: remove after bump is enabled
        const resourcesById = state.resourcesById.filter(
          (transaction) => !deletedAccountIds.includes(transaction.accountId || ''),
        );
        return state.merge({ resourcesById });
      }
      return state;
    },

    // scheduled trandsactions cascade deletion
    [combineActions(deleteScheduledTransaction, deleteScheduledTransactionSuccess).toString()]: (
      state: transactionsTypes.TransactionStore,
      { payload }: { payload: ScheduledTransaction },
    ) =>
      state.merge({
        resourcesById: state.resourcesById.filter(
          (transaction) =>
            transaction.stModelId !== payload.id ||
            transaction.source !== 'SCHEDULED_TRANSACTION_PENDING' ||
            transaction.state !== 'PENDING' ||
            transaction.matchState !== 'NOT_MATCHED',
        ),
      }),

    [actions.performTransactionAction.type]: (state) => state.set('isLoading', true),

    [combineActions(actions.performTransactionActionSuccess).toString()]: (
      state: QcsSyncResourceStore<transactionsTypes.TransactionProps, transactionsTypes.TransactionStoreProps>,
      { payload: resources }: { payload: transactionsTypes.TransactionRecord[] },
    ) => resourceUpserters.upsertResources(state, { resources }, 'default'),
    [actions.applyTransactionsChanges.type]: (state, { payload: resources }) =>
      resourceUpserters.upsertResources(state, { resources, doMerge: false }, 'default'),

    [actions.getTransactionsEarliestDateOnSucess.type]: (state, action) =>
      state.merge({ earliestDateOn: action.payload.dateOn }),

    [actions.initiateA2ATransferSuccess.type]: (state, { payload: resources }) =>
      resourceUpserters.upsertResources(state, { resources }),

    [actions.initiateA2ATransferFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    [combineActions(
      actions.createTransactionFailure,
      actions.updateTransactionFailure,
      actions.deleteTransactionFailure,
      actions.batchTransactionsFailure,
      actions.performTransactionActionFailure,
      deleteScheduledTransactionFailure,
    ).toString()]: (
      state: QcsSyncResourceStore<transactionsTypes.TransactionProps, transactionsTypes.TransactionStoreProps>,
      { payload: error, asyncDispatch }: any,
    ) => {
      asyncDispatch(actions.getTransactions());
      return resourceUpserters.resetResources(state, error);
    },
  },
  transactionsTypes.mkTransactionStore(),
);

export default reducer;
